import { useRef } from 'react';
import { useSelector } from 'react-redux';

import useSubscriptionStatus from 'app/hooks/useSubscriptionStatus';
import { resolveSubscriptionReactivation } from 'app/hooks/subscriptionUpgradeState';
import { useIsJussubClient } from 'app/pages/PlanAndPayment/PlanUpgradeModal/hooks/useJussubPlanOffers';
import { useSubscriptionPlans } from 'app/pages/PlanAndPayment/PlanUpgradeModal/hooks/useSubscriptionPlans';
import { usePlansCatalog } from 'app/hooks/useSubscriptionPlans/usePlansCatalog';
import { rootReducer } from 'redux/rootReducer';

type RootState = ReturnType<typeof rootReducer>;

type ExplorePlansEligibilityState = {
  isEligible: boolean;
  isResolved: boolean;
};

const useExplorePlansEligibilityState = (): ExplorePlansEligibilityState => {
  const subscriptionStatus = useSubscriptionStatus();
  const { hasJusProcessos, jusProcessosQtd } = subscriptionStatus;
  const isUniversalJusfinder = useSelector(
    (state: RootState) => state.universalJusfinder?.isUniversalJusfinder,
  );

  const reactivation = resolveSubscriptionReactivation(subscriptionStatus);
  const isJussubClient = useIsJussubClient();
  const { isReactivationSinglePlan = false, isLoading: isSubscriptionPlansLoading = false } =
    useSubscriptionPlans(hasJusProcessos, jusProcessosQtd, isUniversalJusfinder);

  // Content source of PlanExplorationModal. Its own loading state is the "gate is
  // decided" signal — `is_offered_plan_eligibility_loaded` never resolves anymore since the
  // promotional-offer search was disabled. The catalog list itself is price tiers, not an
  // individual promotional offer, so it never feeds hasOfferedPlans.
  const { isLoading: isPlansCatalogLoading } = usePlansCatalog();

  const offeredPlanEligibility = useSelector(
    (state: RootState) => state.app?.offered_plan_eligibility,
  );
  const offeredPlanItems = Array.isArray(offeredPlanEligibility)
    ? offeredPlanEligibility
    : [];
  const hasOfferedPlans = offeredPlanItems.length > 0;

  const isGateResolved = !isPlansCatalogLoading;

  const isResolved = isGateResolved && !isSubscriptionPlansLoading;
  if (!isResolved) {
    return { isEligible: false, isResolved: false };
  }

  return {
    isEligible:
      !isJussubClient &&
      !hasOfferedPlans &&
      !isReactivationSinglePlan &&
      !reactivation.isUnavailable,
    isResolved: true,
  };
};

export const useExplorePlansEligibility = (): boolean =>
  useExplorePlansEligibilityState().isEligible;

// Latch eligibility while the modal is open so a mid-flow subscription refresh
// (e.g. after checkout) does not remount/hide an already open PlanExplorationModal.
// Keeps tracking the live value until the gate resolves at least once — callers that
// hardcode isModalVisible=true (it never toggles) would otherwise freeze forever at
// the not-yet-resolved "false" from the first render.
export const useStableExplorePlansEligibility = (isModalVisible: boolean): boolean => {
  const { isEligible, isResolved } = useExplorePlansEligibilityState();
  const latchedEligibilityRef = useRef(isEligible);
  const wasModalVisibleRef = useRef(isModalVisible);
  const hasResolvedOnceRef = useRef(isResolved);

  const justOpened = isModalVisible && !wasModalVisibleRef.current;
  if (justOpened || !isModalVisible || !hasResolvedOnceRef.current) {
    latchedEligibilityRef.current = isEligible;
  }
  wasModalVisibleRef.current = isModalVisible;
  if (isResolved) {
    hasResolvedOnceRef.current = true;
  }

  return isModalVisible ? latchedEligibilityRef.current : isEligible;
};
